/**
 * The outline rail (docs/ux/workspace.md): Needs attention with Previous and
 * Next per group, then the sections and question groups with badges that
 * never hide open work. Sticky beside the report column on desktop; on the
 * phone the same pieces become a sticky attention bar and an Outline sheet.
 */

import { cn } from 'cn';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useState } from 'react';
import type { WorkspaceBox, WorkspaceSection, WorkspaceView } from '@/app/types';
import { StateChip } from '@/components/matter/StateChip';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { pluralise } from '@/lib/format';
import { attentionGroups, groupLabel, questionNumberOf, type AttentionGroup, type AttentionKey } from './describe';

export type Stepper = {
  /** Where the person is in each group, once they have stepped. */
  cursor: Partial<Record<AttentionKey, number>>;
  step: (group: AttentionGroup, direction: 1 | -1) => void;
};

export function AttentionGroups({ view, stepper, compact }: { view: WorkspaceView; stepper: Stepper; compact?: boolean }) {
  const groups = attentionGroups(view);
  return (
    <section aria-label="Needs attention" data-slot="attention-groups" className="flex flex-col gap-2">
      <h2 className="eyebrow text-beam-text-muted">Needs attention</h2>
      {groups.length === 0 ? (
        <p role="status" className="text-[13px] text-beam-text-muted">Nothing needs your attention.</p>
      ) : (
        <ul className="flex flex-col">
          {groups.map((g) => {
            const at = stepper.cursor[g.key];
            return (
              <li key={g.key} className={cn('flex items-center gap-2', compact ? 'min-h-12' : 'min-h-9')}>
                <span className="min-w-0 flex-1 truncate text-[13px]">{groupLabel(g)}</span>
                <span className="tabular text-[13px] font-medium">{at === undefined ? g.boxIds.length : `${at + 1} of ${g.boxIds.length}`}</span>
                <Button variant="ghost" size="icon" aria-label={`Previous ${g.one.toLowerCase()}`} className={compact ? 'size-11' : 'size-7'} onClick={() => stepper.step(g, -1)}><ChevronLeftIcon aria-hidden="true" className="size-4" /></Button>
                <Button variant="ghost" size="icon" aria-label={`Next ${g.one.toLowerCase()}`} className={compact ? 'size-11' : 'size-7'} onClick={() => stepper.step(g, 1)}><ChevronRightIcon aria-hidden="true" className="size-4" /></Button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function badgesFor(boxes: WorkspaceBox[]): Array<{ label: string; tone: 'warning' | 'neutral' }> {
  const open = boxes.reduce((n, b) => n + b.suggestions.length, 0);
  const questions = boxes.reduce((n, b) => n + b.clarifications.length, 0);
  const out: Array<{ label: string; tone: 'warning' | 'neutral' }> = [];
  if (open > 0) out.push({ label: `${open} open`, tone: 'warning' });
  if (questions > 0) out.push({ label: pluralise(questions, 'question'), tone: 'warning' });
  return out;
}

export type OutlineProps = {
  view: WorkspaceView;
  currentSection: string | null;
  onSection: (key: string) => void;
  onBox: (boxId: string) => void;
  allFolded: boolean;
  onCollapseAll: () => void;
  onExpandAll: () => void;
  compact?: boolean;
};

export function OutlineList({ view, currentSection, onSection, onBox, allFolded, onCollapseAll, onExpandAll, compact }: OutlineProps) {
  const rowClass = cn('flex w-full items-center gap-2 rounded-md px-2 text-left text-[13px] hover:bg-beam-cream-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-beam-signature', compact ? 'min-h-12 text-[15px]' : 'min-h-8');
  return (
    <nav aria-label="Outline" data-slot="outline" className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="eyebrow text-beam-text-muted">Outline</h2>
        <Button variant="ghost" size="sm" className={compact ? 'h-11' : 'h-8'} onClick={allFolded ? onExpandAll : onCollapseAll}>{allFolded ? 'Expand all' : 'Collapse all'}</Button>
      </div>
      <ul className="flex flex-col">
        {view.sections.filter((s) => !s.parent).map((s) => (
          <li key={s.key} className="flex flex-col">
            <button type="button" aria-current={currentSection === s.key ? 'location' : undefined} className={cn(rowClass, currentSection === s.key && 'border-l-2 border-beam-signature pl-[6px] font-medium')} onClick={() => onSection(s.key)}>
              <span className="min-w-0 flex-1 truncate">{s.label}</span>
              {badgesFor(s.boxes).map((b) => <StateChip key={b.label} label={b.label} tone={b.tone} size="sm" />)}
            </button>
            {s.kind === 'questions' ? <QuestionRows section={s} onBox={onBox} rowClass={rowClass} /> : null}
            <GroupRows parent={s} view={view} currentSection={currentSection} onSection={onSection} rowClass={rowClass} />
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** A group's children (the schedule's categories under the schedule of documents), nested like the question rows. */
function GroupRows({ parent, view, currentSection, onSection, rowClass }: { parent: WorkspaceSection; view: WorkspaceView; currentSection: string | null; onSection: (key: string) => void; rowClass: string }) {
  const children = view.sections.filter((s) => s.parent === parent.key);
  if (children.length === 0) return null;
  return (
    <ul data-slot="outline-group" data-parent={parent.key} className="ml-3 flex flex-col border-l border-beam-line pl-1">
      {children.map((c) => (
        <li key={c.key}>
          <button type="button" aria-current={currentSection === c.key ? 'location' : undefined} className={cn(rowClass, currentSection === c.key && 'border-l-2 border-beam-signature pl-[6px] font-medium')} onClick={() => onSection(c.key)}>
            <span className="min-w-0 flex-1 truncate text-beam-text-soft">{c.label}</span>
            {badgesFor(c.boxes).map((b) => <StateChip key={b.label} label={b.label} tone={b.tone} size="sm" />)}
          </button>
        </li>
      ))}
    </ul>
  );
}

function QuestionRows({ section, onBox, rowClass }: { section: WorkspaceSection; onBox: (boxId: string) => void; rowClass: string }) {
  const groups = new Map<string, WorkspaceBox[]>();
  for (const b of section.boxes) {
    const n = questionNumberOf(b);
    groups.set(n, [...(groups.get(n) ?? []), b]);
  }
  return (
    <ul className="ml-3 flex flex-col border-l border-beam-line pl-1">
      {[...groups.entries()].map(([number, boxes]) => {
        const heading = section.headings.find((h) => h.number === number);
        const first = boxes[0]!;
        const title = heading?.text ?? (boxes.length === 1 ? first.prompt : `Question ${number}`);
        return (
          <li key={number} className="flex flex-col">
            <button type="button" className={rowClass} onClick={() => onBox(first.id)}>
              <span className="tabular w-6 shrink-0 font-medium">{number}</span>
              <span className="min-w-0 flex-1 truncate text-beam-text-soft">{title}</span>
              {badgesFor(boxes).map((b) => <StateChip key={b.label} label={b.label} tone={b.tone} size="sm" />)}
            </button>
            {boxes.length > 1 ? (
              <ul className="ml-6 flex flex-col">
                {boxes.map((b) => (
                  <li key={b.id}>
                    <button type="button" className={rowClass} onClick={() => onBox(b.id)}>
                      <span className="tabular w-8 shrink-0 text-beam-text-muted">{b.label.slice(number.length)}</span>
                      <span className="min-w-0 flex-1 truncate text-beam-text-soft">{b.prompt}</span>
                      {badgesFor([b]).map((x) => <StateChip key={x.label} label={x.label} tone={x.tone} size="sm" />)}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function OutlineRail(props: OutlineProps & { stepper: Stepper }) {
  return (
    <aside data-slot="outline-rail" className="sticky top-[76px] flex max-h-[calc(100vh-100px)] flex-col gap-5 overflow-y-auto rounded-xl border border-beam-line bg-beam-paper p-4">
      <AttentionGroups view={props.view} stepper={props.stepper} />
      <OutlineList {...props} />
    </aside>
  );
}

/** The phone's stepping control: sticky under the top bar, with the Outline sheet behind one button. */
export function AttentionBar(props: OutlineProps & { stepper: Stepper }) {
  const [open, setOpen] = useState(false);
  const groups = attentionGroups(props.view);
  const total = groups.reduce((n, g) => n + g.boxIds.length, 0);
  const first = groups[0];
  const at = first ? props.stepper.cursor[first.key] : undefined;
  return (
    <>
      <div data-slot="attention-bar" className="sticky top-14 z-10 -mx-4 flex min-h-12 items-center gap-1 border-b border-beam-line bg-beam-paper px-3">
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{total === 0 ? 'Nothing to look at' : `${total} to look at`}</span>
        {first ? (
          <>
            <Button variant="ghost" size="icon" className="size-11" aria-label={`Previous ${first.one.toLowerCase()}`} onClick={() => props.stepper.step(first, -1)}><ChevronLeftIcon aria-hidden="true" className="size-5" /></Button>
            <span className="tabular text-[13px]">{at === undefined ? first.boxIds.length : `${at + 1} of ${first.boxIds.length}`}</span>
            <Button variant="ghost" size="icon" className="size-11" aria-label={`Next ${first.one.toLowerCase()}`} onClick={() => props.stepper.step(first, 1)}><ChevronRightIcon aria-hidden="true" className="size-5" /></Button>
          </>
        ) : null}
        <Button variant="outline" className="h-11" onClick={() => setOpen(true)}>Outline</Button>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" showCloseButton={false} className="max-h-[70svh] rounded-t-[20px] shadow-beam-3">
          <div aria-hidden="true" className="mx-auto mt-2 h-1 w-10 rounded-full bg-beam-line-strong" />
          <SheetHeader className="pb-0"><SheetTitle className="text-base">Outline</SheetTitle><SheetDescription className="sr-only">Needs attention and the sections of the report</SheetDescription></SheetHeader>
          <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 pb-6">
            <AttentionGroups view={props.view} stepper={{ cursor: props.stepper.cursor, step: (g, d) => { setOpen(false); props.stepper.step(g, d); } }} compact />
            <OutlineList {...props} compact onSection={(k) => { setOpen(false); props.onSection(k); }} onBox={(id) => { setOpen(false); props.onBox(id); }} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
